import { Injectable, Type } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, forkJoin, Observable, throwError } from 'rxjs';
import { AttributionBudgetModelDto, MutationGetListeAttributionBudgetArgs } from 'src/graphql/generated-types';
import { KalSelectionModel } from '@kalidea/kaligraphi';
import { SubscriptionManager } from 'src/app/_core/tools/subscription-manager';
import { ActionType, ofActionSuccessful, Store } from '@ngxs/store';
import { LayoutService } from 'src/app/_core/services/layout.service';
import {
    SharedGetReferenceMotorDatesListAction,
    SharedGetReferenceYearsListAction
} from 'src/app/_shared/stores/reference-years/reference-years.action';
import {
    SharedSubventionCriteriaGetCriteriaAction
} from 'src/app/_shared/stores/shared-subvention-criteria/shared-subvention-criteria.action';
import { catchError, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { StoreProviderService } from 'src/app/_core/services/store.provider.service';
import isEqual from 'lodash-es/isEqual';
import {
    SharedAllocationExecutionCancelAllocationExecutionAction,
    SharedAllocationExecutionExecuteAllocationAction
} from 'src/app/_shared/stores/shared-allocation-execution/shared-allocation-execution.actions';
import gql from 'graphql-tag';
import { AllocationsContext } from 'src/app/_shared/services/allocations/detail/allocation.service';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { ActivitySalesState } from 'src/app/board/activity/stores/activity-sales/activity-sales.state';

@Injectable({providedIn: 'root'})
export abstract class AllocationsListService extends SubscriptionManager {

    abstract title: string;

    addButtonLabel = `Ajouter une campagne d'attribution`;

    displayArchiveToggleLabel = 'Afficher les campagnes archivées';

    displayedAllocationsList$: BehaviorSubject<AttributionBudgetModelDto[]> = new BehaviorSubject<AttributionBudgetModelDto[]>([]);

    displayArchiveChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    selection = new KalSelectionModel<AttributionBudgetModelDto>();

    /**
     * context de l'attribution
     * Permet de distinguer les différents types pour des questions d'affichages dans les composants
     */
    abstract context: (typeof AllocationsContext)[keyof typeof AllocationsContext];

    /**
     * Définition des actions supplémentaires à dispatch lors de
     * l'initialisation du composant
     */
    initializationActions: Type<any>[] = [];

    /**
     * Définition des actions utilisées pour
     * - Le rechargement de la liste des attributions
     * - La création d'une nouvelle attribution
     * - La récupération d'une attribution
     */
    abstract actions: { reload: ActionType, create: Type<any>, get: Type<any> };

    /**
     * Définition des routes utilisées pour la navigation
     */
    abstract routes: { list: string, create: string, detail: string };

    protected allocationsList$: BehaviorSubject<AttributionBudgetModelDto[]> = new BehaviorSubject<AttributionBudgetModelDto[]>([]);

    protected constructor(protected readonly store: Store,
                          protected readonly layoutService: LayoutService,
                          protected readonly graphqlService: GraphqlService) {
        super();
    }

    navigateToCreation(): void {
        this.selection.clear();
        this.store.dispatch(new this.actions.create());

        this.layoutService.navigate({
            content: this.routes.list,
            focus: this.routes.create
        }, 'focus');
    }

    navigateToDetail(id: number): void {
        this.layoutService.navigate({
            content: this.routes.list,
            focus: `${this.routes.detail}/${id}`
        }, 'focus');
    }

    getAllocationsList(): Observable<AttributionBudgetModelDto[]> {
        this.loading$.next(true);

        return this.getAllocationsBudgetList$().pipe(
            tap(allocationsList => this.allocationsList$.next(allocationsList)),
            tap((a) => this.loading$.next(false)),
            catchError((err) => {
                this.loading$.next(false);
                return throwError(err);
            })
        );
    }

    initialize$(): Observable<any> {
        this.loading$.next(true);
        return this.getAllocationsList().pipe(
            tap(() => this.loading$.next(true)),
            switchMap(() => this.store.dispatch([
                    ...this.initializationActions,
                    new SharedGetReferenceYearsListAction(),
                    new SharedSubventionCriteriaGetCriteriaAction(),
                    new SharedGetReferenceMotorDatesListAction()
                ])
            ),
            switchMap(() => this.initializeListSubscription()),
            tap(() => this.loading$.next(false)),
            switchMap(() => forkJoin([
                this.initializeSelectionSubscription(),
                this.initializeRefreshSubscription(),
                this.initializeExecutionSubscription(),
            ])),
            tap(() => this.loading$.next(false)),
            catchError(err => {
                this.loading$.next(false);
                return throwError(err);
            })
        );
    }

    /**
     * Récupération de la liste des attributions
     */
    private getAllocationsBudgetList$(): Observable<AttributionBudgetModelDto[]> {
        const activityId = this.store.selectSnapshot(ActivitySalesState.currentActivityId) || null;
        return this.graphqlService.mutateOne<'GetListeAttributionBudget', MutationGetListeAttributionBudgetArgs>({
            mutation: gql`
                mutation GetListeAttributionBudget(
                    $contexte: ContextInfosInput,
                    $critere: CriteresAttributionBudgetDTOInput
                ) {
                    GetListeAttributionBudget(
                        contexte: $contexte
                        critere: $critere
                    ) {
                        AttributionBudgetModelDTO {
                            Id
                            Libelle
                            Code
                            IsCloture
                            Description
                        }
                    }
                }
            `,
            variables: {
                critere: {
                    IdActivite: activityId,
                    Context: this.context,
                    IsAbondement: this.context === AllocationsContext.PMV,
                    IsOrderByLibelle: true
                }
            }
        }).pipe(
            map(GetListeAttributionBudget => GetListeAttributionBudget?.AttributionBudgetModelDTO || []),
        );
    }

    private initializeRefreshSubscription(): Observable<any> {
        return StoreProviderService.actions$.pipe(
            ofActionSuccessful(this.actions.reload),
            switchMap(({allocationId}) => {
                return this.getAllocationsList().pipe(
                    map(allocationsList => ({allocationId, allocationsList}))
                );
            }),
            tap(({allocationId, allocationsList}) => {
                const id = allocationId || this.selection.added?.[0]?.Id || null;
                const allocationSelected = allocationsList.filter(({Id}) => Id === id);

                this.selection.clear(false);

                if (arrayNotEmpty(allocationSelected)) {
                    this.selection.select({values: allocationSelected, emitEvent: false});
                }
            }),
        );
    }

    private initializeExecutionSubscription(): Observable<any> {
        return StoreProviderService.actions$.pipe(
            ofActionSuccessful(
                SharedAllocationExecutionExecuteAllocationAction,
                SharedAllocationExecutionCancelAllocationExecutionAction
            ),
            switchMap(() => {
                const allocationId = this.selection?.added?.[0]?.Id;
                return this.store.dispatch(new this.actions.get(allocationId, {force: true}));
            }),
        );
    }

    private initializeListSubscription(): Observable<any> {
        const showArchived$ = this.displayArchiveChanged$.pipe(startWith(false));

        return combineLatest([this.allocationsList$, showArchived$]).pipe(
            distinctUntilChanged(isEqual),
            map(([allocationsList, displayArchived]) => {
                this.displayedAllocationsList$.next(allocationsList.filter(a => displayArchived || !a.IsCloture));
            }),
        );
    }

    private initializeSelectionSubscription(): Observable<any> {
        return this.selection.changes.pipe(
            filter((selection) => arrayNotEmpty(selection.added)),
            tap(({added}) => this.navigateToDetail(added[0]?.Id))
        );
    }
}
